import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Save, Cancel } from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import { TGradeLevel } from "../../../apis/academic/academic.interfaces";
import {
  useCreateGradeLevelHook,
  useUpdateGradeLevelHook,
  useGradeLevelHook,
} from "../../../hooks/useAcademicHook";
import { logger } from "../../../utils/common";
import { useInstituteContext } from "../../../context/institute.context";

const gradeLevelSchema = z
  .object({
    name: z
      .string()
      .min(1, "Grade name is required")
      .min(1, "Grade name must be at least 1 character")
      .max(50, "Grade name must not exceed 50 characters"),
    gradingType: z.enum(["percentage", "score"], {
      errorMap: () => ({ message: "Please select a grading type" }),
    }),
    minPercentage: z.coerce
      .number({
        invalid_type_error: "Minimum percentage must be a number",
      })
      .min(0, "Minimum percentage must be 0 or greater")
      .max(100, "Minimum percentage must not exceed 100")
      .optional()
      .nullable(),
    maxPercentage: z.coerce
      .number({
        invalid_type_error: "Maximum percentage must be a number",
      })
      .min(0, "Maximum percentage must be 0 or greater")
      .max(100, "Maximum percentage must not exceed 100")
      .optional()
      .nullable(),
    minScore: z.coerce
      .number({
        invalid_type_error: "Minimum score must be a number",
      })
      .min(0, "Minimum score must be 0 or greater")
      .optional()
      .nullable(),
    maxScore: z.coerce
      .number({
        invalid_type_error: "Maximum score must be a number",
      })
      .min(0, "Maximum score must be 0 or greater")
      .optional()
      .nullable(),
    description: z
      .string()
      .max(500, "Description must not exceed 500 characters")
      .optional()
      .or(z.literal("")),
  })
  .refine(
    (data) => {
      if (data.gradingType === "percentage") {
        if (
          data.minPercentage === undefined ||
          data.minPercentage === null ||
          data.maxPercentage === undefined ||
          data.maxPercentage === null
        ) {
          return false;
        }
        return data.minPercentage <= data.maxPercentage;
      } else {
        if (
          data.minScore === undefined ||
          data.minScore === null ||
          data.maxScore === undefined ||
          data.maxScore === null
        ) {
          return false;
        }
        return data.minScore <= data.maxScore;
      }
    },
    {
      message: "Minimum value must be less than or equal to maximum value",
      path: ["minPercentage"],
    },
  )
  .refine(
    (data) => {
      if (data.gradingType === "percentage") {
        return true;
      }
      return true;
    },
    {
      message: "Minimum score must be less than or equal to maximum score",
      path: ["minScore"],
    },
  );

type GradeLevelFormData = z.infer<typeof gradeLevelSchema>;

interface ManageGradeLevelDialogProps {
  open: boolean;
  onClose: () => void;
  gradeLevel?: TGradeLevel | null;
}

const ManageGradeLevelDialog: FC<ManageGradeLevelDialogProps> = ({
  open,
  onClose,
  gradeLevel,
}) => {
  const { selectedInstitute } = useInstituteContext();
  const createGradeLevelMutation = useCreateGradeLevelHook();
  const updateGradeLevelMutation = useUpdateGradeLevelHook();
  const { data: gradeLevels } = useGradeLevelHook();
  const isEditMode = !!gradeLevel;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<GradeLevelFormData>({
    resolver: zodResolver(gradeLevelSchema),
    defaultValues: {
      name: "",
      gradingType: "percentage",
      minPercentage: undefined,
      maxPercentage: undefined,
      minScore: undefined,
      maxScore: undefined,
      description: "",
    },
  });

  const watchedGradingType = watch("gradingType");
  const watchedMinPercentage = watch("minPercentage");
  const watchedMaxPercentage = watch("maxPercentage");
  const watchedMinScore = watch("minScore");
  const watchedMaxScore = watch("maxScore");

  useEffect(() => {
    if (gradeLevel) {
      const gradingType =
        gradeLevel.minPercentage !== undefined ? "percentage" : "score";
      reset({
        name: gradeLevel.name,
        gradingType,
        minPercentage: gradeLevel.minPercentage ?? 0,
        maxPercentage: gradeLevel.maxPercentage ?? 100,
        minScore: gradeLevel.minScore ?? undefined,
        maxScore: gradeLevel.maxScore ?? undefined,
        description: gradeLevel.description || "",
      });
    } else {
      reset({
        name: "",
        gradingType: "percentage",
        minPercentage: undefined,
        maxPercentage: undefined,
        minScore: undefined,
        maxScore: undefined,
        description: "",
      });
    }
  }, [gradeLevel, reset, open]);

  const validateNoOverlap = (): boolean => {
    if (!gradeLevels) return true;
    const currentMin =
      watchedGradingType === "percentage"
        ? watchedMinPercentage
        : watchedMinScore;
    const currentMax =
      watchedGradingType === "percentage"
        ? watchedMaxPercentage
        : watchedMaxScore;

    if (currentMin === undefined || currentMax === undefined) return true;

    // Check for overlap with other grade levels of the same type
    return !gradeLevels.some((gl) => {
      if (gl.id === gradeLevel?.id) return false;

      // Only check overlap with same grading type
      const glHasPercentage =
        gl.minPercentage !== undefined || gl.maxPercentage !== undefined;
      const glHasScore = gl.minScore !== undefined || gl.maxScore !== undefined;
      const currentIsPercentage = watchedGradingType === "percentage";

      // Skip if different grading types
      if (currentIsPercentage && !glHasPercentage) return false;
      if (!currentIsPercentage && !glHasScore) return false;

      const glMin = gl.minPercentage ?? gl.minScore;
      const glMax = gl.maxPercentage ?? gl.maxScore;
      if (glMin === undefined || glMax === undefined) return false;

      // Check for overlap
      if (
        currentMin === null ||
        currentMin === undefined ||
        currentMax === null ||
        currentMax === undefined
      ) {
        return false;
      }
      return currentMin <= glMax && currentMax >= glMin;
    });
  };

  const validateSequencing = (): { isValid: boolean; message?: string } => {
    if (!gradeLevels || gradeLevels.length === 0) return { isValid: true };

    const currentMin =
      watchedGradingType === "percentage"
        ? watchedMinPercentage
        : watchedMinScore;
    const currentMax =
      watchedGradingType === "percentage"
        ? watchedMaxPercentage
        : watchedMaxScore;

    if (currentMin === undefined || currentMax === undefined)
      return { isValid: true };

    // Get all grade levels of the same type, sorted by max value (descending)
    const sameTypeLevels = gradeLevels
      .filter((gl) => {
        if (gl.id === gradeLevel?.id) return false;
        const glHasPercentage =
          gl.minPercentage !== undefined || gl.maxPercentage !== undefined;
        const glHasScore =
          gl.minScore !== undefined || gl.maxScore !== undefined;
        return watchedGradingType === "percentage"
          ? glHasPercentage
          : glHasScore;
      })
      .map((gl) => ({
        id: gl.id,
        name: gl.name,
        min: gl.minPercentage ?? gl.minScore ?? 0,
        max: gl.maxPercentage ?? gl.maxScore ?? 0,
      }))
      .sort((a, b) => b.max - a.max); // Sort by max descending (highest first)

    if (sameTypeLevels.length === 0) return { isValid: true };

    // Check if current grade level fits logically in the sequence
    // For percentage-based: higher percentages should be better grades
    // For score-based: higher scores should be better grades
    // We'll check if the new grade level creates a logical sequence

    // Find where this grade level would fit
    const higherGrades = sameTypeLevels.filter(
      (gl) =>
        currentMax !== null && currentMax !== undefined && gl.max >= currentMax,
    );
    const lowerGrades = sameTypeLevels.filter(
      (gl) =>
        currentMin !== null && currentMin !== undefined && gl.max < currentMin,
    );

    // If there are higher grades, current max should be less than the lowest higher grade's min
    if (higherGrades.length > 0) {
      const lowestHigherGrade = higherGrades.reduce(
        (min, gl) => (gl.min < min.min ? gl : min),
        higherGrades[0],
      );
      if (
        currentMax !== null &&
        currentMax !== undefined &&
        currentMax >= lowestHigherGrade.min
      ) {
        return {
          isValid: false,
          message: `This grade level overlaps or conflicts with "${lowestHigherGrade.name}". Higher grade levels should have higher ranges.`,
        };
      }
    }

    // If there are lower grades, current min should be greater than the highest lower grade's max
    if (lowerGrades.length > 0) {
      const highestLowerGrade = lowerGrades.reduce(
        (max, gl) => (gl.max > max.max ? gl : max),
        lowerGrades[0],
      );
      if (
        currentMin !== null &&
        currentMin !== undefined &&
        currentMin <= highestLowerGrade.max
      ) {
        return {
          isValid: false,
          message: `This grade level overlaps or conflicts with "${highestLowerGrade.name}". Lower grade levels should have lower ranges.`,
        };
      }
    }

    return { isValid: true };
  };

  const gradingTypeOptions = [
    { key: "percentage", value: "Percentage-based" },
    { key: "score", value: "Score-based" },
  ];

  const onSubmit = async (data: GradeLevelFormData) => {
    if (!selectedInstitute) {
      logger.show_error(
        "Please select an institute from the dashboard before creating or editing a grade level.",
      );
      return;
    }

    // Validate range values
    if (data.gradingType === "percentage") {
      if (
        data.minPercentage === undefined ||
        data.minPercentage === null ||
        data.maxPercentage === undefined ||
        data.maxPercentage === null
      ) {
        logger.show_error(
          "Both minimum and maximum percentage values are required for percentage-based grading.",
        );
        return;
      }
      if (data.minPercentage > data.maxPercentage) {
        logger.show_error(
          `Minimum percentage (${data.minPercentage}%) cannot be greater than maximum percentage (${data.maxPercentage}%).`,
        );
        return;
      }
      if (data.minPercentage < 0 || data.maxPercentage > 100) {
        logger.show_error("Percentage values must be between 0 and 100.");
        return;
      }
    } else {
      if (
        data.minScore === undefined ||
        data.minScore === null ||
        data.maxScore === undefined ||
        data.maxScore === null
      ) {
        logger.show_error(
          "Both minimum and maximum score values are required for score-based grading.",
        );
        return;
      }
      if (data.minScore > data.maxScore) {
        logger.show_error(
          `Minimum score (${data.minScore}) cannot be greater than maximum score (${data.maxScore}).`,
        );
        return;
      }
      if (data.minScore < 0) {
        logger.show_error("Score values must be 0 or greater.");
        return;
      }
    }

    // Validate no overlaps
    if (!validateNoOverlap()) {
      logger.show_error(
        "Grade level ranges cannot overlap with existing grade levels of the same type. Please adjust the range values.",
      );
      return;
    }

    // Validate sequencing
    const sequencingValidation = validateSequencing();
    if (!sequencingValidation.isValid) {
      logger.show_error(
        sequencingValidation.message ||
        "Grade level sequencing is invalid. Higher grade levels should have higher ranges.",
      );
      return;
    }

    try {
      if (isEditMode && gradeLevel) {
        await updateGradeLevelMutation.mutateAsync({
          id: gradeLevel.id,
          request: {
            name: data.name,
            minPercentage:
              data.gradingType === "percentage"
                ? (data.minPercentage ?? undefined)
                : undefined,
            maxPercentage:
              data.gradingType === "percentage"
                ? (data.maxPercentage ?? undefined)
                : undefined,
            minScore:
              data.gradingType === "score"
                ? (data.minScore ?? undefined)
                : undefined,
            maxScore:
              data.gradingType === "score"
                ? (data.maxScore ?? undefined)
                : undefined,
            description: data.description || undefined,
          },
        });
        logger.show_success("Grade level updated successfully!");
      } else {
        await createGradeLevelMutation.mutateAsync({
          instituteId: selectedInstitute.id,
          name: data.name,
          minPercentage:
            data.gradingType === "percentage"
              ? (data.minPercentage ?? undefined)
              : undefined,
          maxPercentage:
            data.gradingType === "percentage"
              ? (data.maxPercentage ?? undefined)
              : undefined,
          minScore:
            data.gradingType === "score"
              ? (data.minScore ?? undefined)
              : undefined,
          maxScore:
            data.gradingType === "score"
              ? (data.maxScore ?? undefined)
              : undefined,
          description: data.description || undefined,
        });
        logger.show_success("Grade level created successfully!");
      }
      onClose();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : `Failed to ${isEditMode ? "update" : "create"} grade level. Please try again.`,
      );
    }
  };

  const isLoading =
    isSubmitting ||
    createGradeLevelMutation.isPending ||
    updateGradeLevelMutation.isPending;
  const isError =
    createGradeLevelMutation.isError || updateGradeLevelMutation.isError;
  const errorMessage =
    createGradeLevelMutation.error?.message ||
    updateGradeLevelMutation.error?.message;
  const overlapError = !validateNoOverlap()
    ? "Grade level ranges cannot overlap with existing grade levels of the same type"
    : undefined;
  const sequencingValidation = validateSequencing();
  const sequencingError = !sequencingValidation.isValid
    ? sequencingValidation.message
    : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="manage-grade-level-dialog"
    >
      <DialogTitle data-testid="manage-grade-level-dialog-title">
        {isEditMode ? "Edit Grade Level" : "Add New Grade Level"}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="grade-level-form">
        <DialogContent data-testid="grade-level-form-content">
          {isError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              data-testid="grade-level-form-error"
            >
              {errorMessage ||
                `Failed to ${isEditMode ? "update" : "create"} grade level. Please try again.`}
            </Alert>
          )}

          {overlapError && (
            <Alert
              severity="warning"
              sx={{ mb: 3 }}
              data-testid="grade-level-overlap-warning"
            >
              {overlapError}
            </Alert>
          )}
          {sequencingError && !overlapError && (
            <Alert
              severity="warning"
              sx={{ mb: 3 }}
              data-testid="grade-level-sequencing-warning"
            >
              {sequencingError}
            </Alert>
          )}

          <Grid
            container
            spacing={3}
            sx={{ mt: 1 }}
            data-testid="grade-level-form-container"
          >
            <ControlledTextField
              control={control as any}
              name="name"
              id="grade-level-name"
              label="Grade Name"
              required
              errorMessage={errors.name?.message}
              inputProps={{ "data-testid": "grade-level-name-input" }}
            />

            <ControlledSelectField
              control={control as any}
              name="gradingType"
              id="grade-level-type"
              label="Grading Type"
              itemList={gradingTypeOptions}
              isRequired
              errorMessage={errors.gradingType?.message}
            />

            {watchedGradingType === "percentage" ? (
              <ControlledTextField
                control={control as any}
                name="minPercentage"
                id="grade-level-min-percentage"
                label="Minimum Percentage"
                type="number"
                required
                errorMessage={errors.minPercentage?.message}
                inputProps={{
                  "data-testid": "grade-level-min-percentage-input",
                  min: 0,
                  max: watchedMaxPercentage ?? 100,
                }}
                sx={{
                  width: "100%",
                }}
              />
            ) : (
              <ControlledTextField
                control={control as any}
                name="minScore"
                id="grade-level-min-score"
                label="Minimum Score"
                type="number"
                required
                errorMessage={errors.minScore?.message}
                inputProps={{
                  "data-testid": "grade-level-min-score-input",
                  min: 0,
                }}
                sx={{
                  width: "100%",
                }}
              />
            )}

            {watchedGradingType === "percentage" ? (
              <ControlledTextField
                control={control as any}
                name="maxPercentage"
                id="grade-level-max-percentage"
                label="Maximum Percentage"
                type="number"
                required
                errorMessage={errors.maxPercentage?.message}
                inputProps={{
                  "data-testid": "grade-level-max-percentage-input",
                  min: watchedMinPercentage ?? 0,
                  max: 100,
                }}
                sx={{
                  width: "100%",
                }}
              />
            ) : (
              <ControlledTextField
                control={control as any}
                name="maxScore"
                id="grade-level-max-score"
                label="Maximum Score"
                type="number"
                required
                errorMessage={errors.maxScore?.message}
                inputProps={{
                  "data-testid": "grade-level-max-score-input",
                  min: 0,
                }}
                sx={{
                  width: "100%",
                }}
              />
            )}

            <ControlledTextField
              control={control as any}
              name="description"
              id="grade-level-description"
              label="Description"
              multiline
              rows={3}
              errorMessage={errors.description?.message}
              inputProps={{ "data-testid": "grade-level-description-input" }}
            />
          </Grid>
        </DialogContent>
        <DialogActions data-testid="grade-level-dialog-actions">
          <Button
            onClick={onClose}
            disabled={isLoading}
            startIcon={<Cancel />}
            data-testid="cancel-grade-level-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
            data-testid="save-grade-level-button"
          >
            {isLoading
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update Grade Level"
                : "Create Grade Level"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageGradeLevelDialog;
